/* eslint-disable clubmed/no-fake-component */
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { isEmpty } from 'lodash';
import { ICONS } from 'Components/utils/icon/icons';
import { constants } from '@clubmed/utils';
import { getPriceDiff } from 'Utils/proposal/proposalUtils';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { Icon } from 'Components/frameworks/icon/Icon';
import { RadioButton } from 'Components/frameworks/form-inputs/radio-button/RadioButton';
import { Text } from 'Components/frameworks/text/Text';
import { Tooltip } from 'Components/frameworks/tooltip/Tooltip';
import { UnmountClosed } from 'react-collapse';
import { themes } from './FlexRates.themes';
const { FLEX_RATES } = constants;
export const getFlexRatesOptions = (
  { price, alternativePrice }: Proposal,
  appContext: AppContext,
  theme: keyof typeof themes,
) => {
  function getPriceOffers({ discounts = [] }: ProposalPrice) {
    return discounts.reduce((acc, { offerId }) => {
      if (offerId) {
        return [...acc, offerId];
      }

      return acc;
    }, []);
  }

  const priceOffers = getPriceOffers(price);
  const isNRR = price.selectedRate === FLEX_RATES.NRR;
  const isStandard = price.selectedRate === FLEX_RATES.STANDARD;
  const isFLR = price.selectedRate === FLEX_RATES.FLR;
  const isEBB = price.selectedRate === FLEX_RATES.EBB;
  const isUsa = appContext.locale === 'en-US';
  const isStandardRateAloneAuthorized = isFeatureFlipped(appContext)('booking.standardRateAlone');

  if (!isEmpty(alternativePrice) && alternativePrice.total.value != null) {
    const rateDiff = getPriceDiff(
      alternativePrice.total.value,
      price.total.value,
      appContext.locale,
      price.currency,
    );
    const alternativePriceOffers = getPriceOffers(alternativePrice);
    const nrrOffers = isNRR ? priceOffers : alternativePriceOffers;
    const standardOffers = isStandard ? priceOffers : alternativePriceOffers;
    const flrOffers = isFLR ? priceOffers : alternativePriceOffers;
    const ebbOffers = isEBB ? priceOffers : alternativePriceOffers;
    const nonRefundableRate = {
      bullets: [
        t('booking.accommodation.packageContent.nonRefundableRateBestPrice'),
        t('booking.accommodation.packageContent.nonRefundableRateCancellation'),
      ],
      className: themes[theme].thNrr,
      dataTestId: 'flexRates-nrr',
      id: FLEX_RATES.NRR,
      offers: nrrOffers,
      price: isNRR ? t('common.included') : rateDiff,
      title: t('booking.accommodation.packageContent.nonRefundableRateLabel').replace('\\n', '\n'),
      tooltipContent: t('booking.accommodation.packageContent.nonRefundableRateLegal'),
    };
    const standardRate = {
      bullets: [
        t('booking.accommodation.packageContent.standardBestPrice'),
        t('booking.accommodation.packageContent.standardCancellation'),
      ],
      className: themes[theme].thStandard,
      dataTestId: 'flexRates-standard',
      id: FLEX_RATES.STANDARD,
      offers: standardOffers,
      price: isStandard ? t('common.included') : rateDiff,
      title: t('booking.accommodation.packageContent.standardLabel'),
      tooltipContent: t('booking.accommodation.packageContent.standardLegal'),
    };
    const ebbRate = {
      bullets: [
        t('booking.accommodation.packageContent.standardBestPrice'),
        t('booking.accommodation.packageContent.standardCancellation'),
      ],
      className: themes[theme].thStandard,
      dataTestId: 'flexRates-ebb',
      id: FLEX_RATES.EBB,
      offers: ebbOffers,
      price: ebbOffers ? t('common.included') : rateDiff,
      title: t('booking.accommodation.packageContent.standardLabel'),
      tooltipContent: t('booking.accommodation.packageContent.standardLegal'),
    };
    const flexibleRate = {
      bullets: [
        t('booking.accommodation.packageContent.flexibleRateCancellation2'),
        t('booking.accommodation.packageContent.flexibleRateCancellation'),
      ],
      className: themes[theme].thNrr,
      dataTestId: 'flexRates-flr',
      id: FLEX_RATES.FLR,
      offers: flrOffers,
      price: isFLR ? t('common.included') : rateDiff,
      title: t('booking.accommodation.packageContent.flexibleRateLabel'),
      tooltipContent: t('booking.accommodation.packageContent.flexibleRateLegal'),
    };

    if (isUsa) {
      if (new Set([...priceOffers, ...alternativePriceOffers]).has('EBB')) {
        return [ebbRate, flexibleRate];
      }

      if (new Set([...priceOffers, ...alternativePriceOffers]).has('FLR')) {
        return [standardRate, flexibleRate];
      }
    }

    return [nonRefundableRate, standardRate];
  }

  if ((isEmpty(alternativePrice) || alternativePrice.total.value == null) && isEBB && isUsa) {
    return [
      {
        bullets: [
          t('booking.accommodation.packageContent.standardBestPrice'),
          t('booking.accommodation.packageContent.standardCancellation'),
        ],
        className: themes[theme].thStandard,
        dataTestId: 'flexRates-ebb',
        id: FLEX_RATES.EBB,
        offers: priceOffers,
        price: t('common.included'),
        title: t('booking.accommodation.packageContent.standardLabel'),
        tooltipContent: t('booking.accommodation.packageContent.standardLegal'),
      },
    ];
  }

  if (
    (isEmpty(alternativePrice) || alternativePrice.total.value == null) &&
    isStandard &&
    !isUsa &&
    isStandardRateAloneAuthorized
  ) {
    return [
      {
        bullets: [
          t('booking.accommodation.packageContent.standardBestPrice'),
          t('booking.accommodation.packageContent.standardCancellation'),
        ],
        className: themes[theme].thStandard,
        dataTestId: 'flexRates-standard',
        id: FLEX_RATES.STANDARD,
        offers: priceOffers,
        price: t('common.included'),
        title: t('booking.accommodation.packageContent.standardLabel'),
        tooltipContent: t('booking.accommodation.packageContent.standardLegal'),
      },
    ];
  }

  return [];
};
type Props = {
  className?: string;
  isInMoreRoomLayer?: boolean;
  isLoading: boolean;
  isMobile?: boolean;
  proposal: Proposal;
  selectedRate: string;
  setSelectedRate: (arg0: string) => void;
  switchOffers?: (offers: Array<string>) => void;
  theme?: keyof typeof themes;
};
export const FlexRates = ({
  className,
  isInMoreRoomLayer,
  isLoading,
  isMobile,
  proposal,
  selectedRate,
  setSelectedRate,
  switchOffers,
  theme = 'default',
}: Props) => {
  const appContext = useAppContext();
  const options = getFlexRatesOptions(proposal, appContext, theme);
  useEffect(() => {
    if (proposal.price.selectedRate !== selectedRate) {
      setSelectedRate(proposal.price.selectedRate);
    }
  }, [proposal.price.selectedRate]);
  const { thRoot, thTitle } = themes[theme];
  return (
    <Fragment>
      {options.length > 1 && (
        <p className={thTitle}>{t('booking.accommodation.packageContent.chooseFlexibility')}</p>
      )}
      <div
        className={classnames(
          thRoot,
          'gap-15',
          {
            'sm:flex-col': isInMoreRoomLayer,
          },
          className,
        )}
      >
        {options.map((option) => (
          <Rate
            {...option}
            isChecked={option.id === selectedRate}
            isLoading={isLoading}
            isMobile={isMobile}
            isInMoreRoomLayer={isInMoreRoomLayer}
            key={option.id}
            onClick={() => {
              if (!switchOffers) {
                setSelectedRate(option.id);
              } else if (option.id !== proposal.price.selectedRate) {
                switchOffers(option.offers);
                setSelectedRate(option.id);
              }
            }}
          />
        ))}
      </div>
    </Fragment>
  );
};

const Rate = ({
  bullets,
  className,
  dataTestId,
  isChecked,
  isLoading,
  onClick,
  price,
  title,
  tooltipContent,
  isMobile,
  isInMoreRoomLayer,
}) => {
  const rootRef = useRef();
  const isMobileOrInMoreRoomLayer = isMobile || isInMoreRoomLayer;
  const [isOpened, setIsOpened] = useState(false);

  const tooltip = () => (
    <Tooltip // @ts-expect-error TODO(flow-migration) does not understand children prop comes from nested cmp
      modalProps={{
        ariaLabel: title,
        closeLabel: t('common.close'),
        size: 'small',
      }}
      renderTrigger={(toggle) => (
        <button
          className="leading-0 text-mediumGrey"
          data-testid="flexRatesTooltipTrigger"
          onClick={(e) => {
            // prevent click on the tooltip icon to select the rate
            e.stopPropagation();
            e.preventDefault();
            toggle();
          }}
          type="button"
        >
          <Icon name={ICONS.INFORMATION} width="1rem" />
        </button>
      )}
    >
      <Text
        className="p-20"
        data-testid="flexRatesTooltipContent"
        text={tooltipContent}
        theme="text-sans-n2"
      />
    </Tooltip>
  );

  return (
    <button
      className={classnames(
        'text-initial relative flex min-h-70 w-full cursor-pointer flex-wrap rounded-12 border-1 p-10',
        {
          'border-lightGrey': !isChecked,
          'border-black': isChecked,
        },
        className,
      )}
      data-testid={dataTestId}
      onClick={(event) => {
        // prevent click inside the modal to select the rate
        if (rootRef.current && rootRef.current.contains(event.target)) {
          onClick();
        }
      }}
      ref={rootRef}
      type="button"
    >
      <div
        className={classnames({
          'flex w-full': !isMobileOrInMoreRoomLayer,
          'w-full': isMobileOrInMoreRoomLayer,
        })}
      >
        <div
          className={classnames('flex w-full gap-10', {
            'items-center': isMobileOrInMoreRoomLayer,
            'items-start': !isMobileOrInMoreRoomLayer,
          })}
        >
          <RadioButton isChecked={isChecked} isDisabled={isLoading} />
          <div
            className={classnames({
              'flex w-full items-center gap-5': isMobileOrInMoreRoomLayer,
            })}
          >
            <Text
              className={classnames('font-bold', {
                'flex-1': isMobileOrInMoreRoomLayer,
              })}
              text={title}
              theme="text-sans-n1"
            />
            <p
              className={classnames('w-full text-16 font-bold text-sienna', {
                'flex-1 text-right': isMobileOrInMoreRoomLayer,
              })}
            >
              {price}
            </p>
            {isMobileOrInMoreRoomLayer && tooltip()}
          </div>
          {!isMobileOrInMoreRoomLayer && tooltip()}
        </div>
        {isMobileOrInMoreRoomLayer ? (
          <UnmountClosed
            isOpened={isOpened}
            theme={{
              collapse: 'collapse-transition px-10 w-full',
              content: 'pt-20',
            }}
          >
            <ul
              className={classnames('mb-20 w-full space-y-5 text-14', {
                'ms-30': !isMobileOrInMoreRoomLayer,
              })}
            >
              {bullets.map((bullet) => (
                <li key={bullet} className="flex items-baseline gap-10">
                  <Icon className="shrink-0 text-green" name={ICONS.CHECK} width="0.75rem" />
                  {bullet}
                </li>
              ))}
            </ul>
          </UnmountClosed>
        ) : (
          <ul
            className={classnames('mb-20 w-full space-y-5 text-14', {
              'ms-30': !isMobileOrInMoreRoomLayer,
            })}
          >
            {bullets.map((bullet) => (
              <li key={bullet} className="flex items-baseline gap-10">
                <Icon className="shrink-0 text-green" name={ICONS.CHECK} width="0.75rem" />
                {bullet}
              </li>
            ))}
          </ul>
        )}
        {isMobileOrInMoreRoomLayer && (
          <div
            className="absolute bottom-0 left-0 w-full text-center"
            onClick={(e) => {
              // prevent click on the arrow icon to select the rate
              e.stopPropagation();
              e.preventDefault();
              setIsOpened(!isOpened);
            }}
          >
            <button data-testid="flex-rates-arrow-icon" type="button">
              <Icon name={ICONS.ARROW_DOWN} rotation={isOpened ? 180 : 0} width="0.50rem" />
            </button>
          </div>
        )}
      </div>
    </button>
  );
};
